import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { colors } from '@guryou/colors';
import { generateWidgetPaymentIntent, useEffectOnSuccess } from '@guryou/redux';
import { printPrice } from '@guryou/utils';
import { isValidEmail } from '@guryou/utils/build/stringUtils';
import { WidgetContext } from './WidgetContext';
import { Modal, Icon } from '../../components/shared';

const WidgetMembershipDetail = ({ membership, openedMembershipId, setOpenedMembershipId }) => {
  const { paymentOptions, primaryColor } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  const secondary = primaryColor || colors.cabaret;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);

  // Personal data validation schema (use GENERAL keys for translations)
  const personalDataSchema = z.object({
    firstname: z.string().min(1, 'nameRequired'),
    lastname: z.string().min(1, 'surnameRequired'),
    email: z
      .string()
      .min(1, 'emailRequired')
      .email('emailRequired'),
    phone: z.string().min(1, 'phoneRequired'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(personalDataSchema) });

  const membershipOnline = paymentOptions?.data?.membershipOnline;
  const membershipOnsite = paymentOptions?.data?.membershipOnsite;

  useEffectOnSuccess([state => state.payments.paymentIntent], () => {
    setOpenedMembershipId(null);
  });

  if (!membership) {
    return null;
  }

  const onSubmit = data => {
    if (!isValidEmail(data.email)) {
      return;
    }
    dispatch(
      generateWidgetPaymentIntent({
        email: data.email,
        firstname: data.firstname,
        lastname: data.lastname,
        membershipId: openedMembershipId,
        phone: data.phone,
      })
    );
  };

  const closeModal = () => {
    setOpenedMembershipId(null);
    setOpen(false);
  };

  const renderModal = () => {
    if (!open) {
      return null;
    }

    return (
      <Modal isOpen={true} onClose={closeModal} title={membership?.name} maxWidth="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6 p-6">
            <div
              className="relative overflow-hidden rounded-2xl p-6 mb-8 shadow-lg"
              style={{ background: `linear-gradient(to bottom right, ${primary}, ${secondary})` }}
            >
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <Icon name="euro" size="md" color="white" />
                    </div>
                    <div>
                      <p className="text-white/90 text-sm font-medium">{t('GENERAL.price')}</p>
                      <p className="text-white/70 text-xs">{t('MEMBERSHIPS.title')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-white">{printPrice(membership.price)}</span>
                    <p className="text-white/80 text-xs mt-1">
                      {membership.sessionsCounter} {t('GENERAL.session')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Step 2: Personal Data */}
            <div className="relative">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-4 text-white" style={{ backgroundColor: primary }}>
                  <Icon name="info" size="sm" className="text-current" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('SERVICES.personalData')}</h3>
                  <p className="text-sm text-gray-600">{t('SERVICES.personalDataStepDescription')}</p>
                </div>
              </div>

              <div className="ml-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.name')} *</label>
                    <input
                      type="text"
                      {...register('firstname')}
                      style={{
                        borderColor: '#e5e7eb',
                        color: colors.mineShaft,
                      }}
                      className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                      onFocus={e => {
                        e.currentTarget.style.borderColor = primary;
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                      }}
                      onBlur={e => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      placeholder={t('GENERAL.namePlaceholder')}
                    />
                    {errors.firstname && (
                      <p className="mt-1 text-sm" style={{ color: primary }}>
                        {t(`GENERAL.${errors.firstname.message}`)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.surname')} *</label>
                    <input
                      type="text"
                      {...register('lastname')}
                      style={{
                        borderColor: '#e5e7eb',
                        color: colors.mineShaft,
                      }}
                      className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                      onFocus={e => {
                        e.currentTarget.style.borderColor = primary;
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                      }}
                      onBlur={e => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      placeholder={t('GENERAL.surnamePlaceholder')}
                    />
                    {errors.lastname && (
                      <p className="mt-1 text-sm" style={{ color: primary }}>
                        {t(`GENERAL.${errors.lastname.message}`)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.email')} *</label>
                    <input
                      type="email"
                      {...register('email')}
                      style={{
                        borderColor: '#e5e7eb',
                        color: colors.mineShaft,
                      }}
                      className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                      onFocus={e => {
                        e.currentTarget.style.borderColor = primary;
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                      }}
                      onBlur={e => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      placeholder={t('GENERAL.emailPlaceholder')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm" style={{ color: primary }}>
                        {t(`GENERAL.${errors.email.message}`)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.phone')} *</label>
                    <input
                      type="tel"
                      {...register('phone')}
                      style={{
                        borderColor: '#e5e7eb',
                        color: colors.mineShaft,
                      }}
                      className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                      onFocus={e => {
                        e.currentTarget.style.borderColor = primary;
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                      }}
                      onBlur={e => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      placeholder={t('GENERAL.phonePlaceholder')}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm" style={{ color: primary }}>
                        {t(`GENERAL.${errors.phone.message}`)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: `linear-gradient(to right, ${primary}, ${secondary})`,
                }}
                className={`flex-1 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    {t('GENERAL.loading')}
                  </>
                ) : (
                  <>
                    <Icon name="shopping-cart" size="sm" className="mr-2" />
                    {t('GENERAL.payNow')} {printPrice(membership.price)}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    );
  };

  return (
    <div>
      {/* Match service card container: gradient, decorative shapes, border/hover */}
      <div className="bg-gradient-to-br from-springWood to-white rounded-2xl p-6 hover:shadow-xl hover:shadow-mandy/20 transition-all duration-300 relative overflow-hidden border border-mercury shadow-sm hover:border-mandy/30 w-full max-w-sm">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full" style={{ backgroundColor: primary, opacity: 0.1 }}></div>
        <div className="absolute bottom-0 left-0 w-20 h-20 rounded-tr-full" style={{ backgroundColor: primary, opacity: 0.1 }}></div>

        {/* Card Content */}
        <div className="relative z-10">
          <h5 className={`text-xl font-semibold max-w-md truncate text-[${colors.mineShaft}]`}>{membership?.name}</h5>
          <p className={`mb-5 text-sm`} style={{ color: colors.boulder }}>
            {membership?.description}
          </p>
          <div className="flex items-baseline mt-2" style={{ color: colors.mineShaft }}>
            <span className="text-sm" style={{ color: colors.boulder }}>
              {t('MEMBERSHIPS.numberOfSessions')}: {membership.sessionsCounter || '0'}
            </span>
          </div>
          <div role="list" className="space-y-5 my-7"></div>
          {(membershipOnline || membershipOnsite) && (
            <div className="flex items-center justify-between">
              {/* Price */}
              <div className="text-3xl font-bold" style={{ color: colors.mineShaft }}>
                {membership?.price} €
              </div>
              <button
                onClick={() => {
                  setOpen(true);
                  setOpenedMembershipId(membership.id);
                }}
                style={{
                  background: `linear-gradient(to right, ${primary}, ${secondary})`,
                }}
                className="text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center shadow-lg hover:shadow-xl"
                onMouseEnter={e => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${secondary}, ${primary})`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = `linear-gradient(to right, ${primary}, ${secondary})`;
                }}
              >
                <Icon name="shopping-cart" size="sm" className="mr-2" />
                {t('GENERAL.payNow')}
              </button>
            </div>
          )}
        </div>
      </div>
      {renderModal()}
    </div>
  );
};

export default WidgetMembershipDetail;
